import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";

interface CalculatorWorkbenchProps {
  inputs: ReactNode;
  results: ReactNode;
  resultsLabel?: string;
}

export function CalculatorWorkbench({
  inputs,
  results,
  resultsLabel = "Estimate",
}: CalculatorWorkbenchProps) {
  return (
    <Card padding="none" className="@container/calculator">
      <div className="grid @4xl/calculator:grid-cols-[minmax(0,1fr)_minmax(300px,380px)]">
        <div className="@container/inputs min-w-0 space-y-8 p-5 @lg/calculator:p-6">
          {inputs}
        </div>
        <aside
          aria-label={resultsLabel}
          className="min-w-0 border-t border-border bg-muted/40 p-5 @lg/calculator:p-6 @4xl/calculator:border-t-0 @4xl/calculator:border-l"
        >
          <div className="space-y-5 @4xl/calculator:sticky @4xl/calculator:top-6">
            <h2 className="text-overline uppercase text-muted-foreground">
              {resultsLabel}
            </h2>
            <div aria-live="polite" className="space-y-5">
              {results}
            </div>
          </div>
        </aside>
      </div>
    </Card>
  );
}

interface CalculatorSectionProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}

export function CalculatorSection({ title, action, children }: CalculatorSectionProps) {
  return (
    <div role="group" aria-label={title} className="space-y-4">
      <div className="flex items-end justify-between gap-4 border-b border-border pb-2">
        <h3 className="min-w-0 text-sm font-semibold text-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export function CalculatorFields({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-4 @md/inputs:grid-cols-2 @2xl/inputs:grid-cols-3">
      {children}
    </div>
  );
}

export function CalculatorNote({ children }: { children: ReactNode }) {
  return <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>;
}
